import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { encrypt } from "../../utils/encryption";
import apiClient from "../../services/apiClient";
import { normalizeApiError } from "../../utils/apiError";
import Button from "../../components/common/Button";
import CrudForm from "../../components/common/crud/CrudForm";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";
import alertService from "../../utils/alertService";

const currencyUomFormSchema = [
  {
    name: "currencyName",
    label: "Currency Name",
    type: "text",
    placeholder: "e.g. Indian Rupee",
    validation: {
      required: true,
      minLength: 2,
      maxLength: 100,
    },
  },
  {
    name: "currencyCode",
    label: "Currency Code",
    type: "text",
    placeholder: "e.g. INR",
    validation: {
      required: true,
      minLength: 3,
      maxLength: 3,
      pattern: /^[A-Za-z]{3}$/,
      patternMessage: "Currency Code must contain exactly 3 letters (ISO 4217).",
    },
  },
  {
    name: "symbol",
    label: "Symbol",
    type: "text",
    placeholder: "e.g. ₹",
    validation: {
      required: true,
      maxLength: 10,
    },
  },
  {
    name: "isActive",
    label: "Active",
    type: "checkbox",
    defaultValue: true,
  },
];

function CurrencyUomModulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useEncryptedParams();
  const [editValues, setEditValues] = useState(null);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [formError, setFormError] = useState(null);

  const isCreatePage = location.pathname.endsWith("/new");
  const isEditPage = Boolean(id) && (location.pathname.endsWith("/edit") || location.pathname.includes(`/${id}`));
  const isFormPage = isCreatePage || isEditPage;
  const formMode = isEditPage ? "edit" : "create";

  // Base path for navigation within this module
  const basePath = useMemo(() => {
    if (location.pathname.includes("/settings-master/currency-uom")) return "/settings-master/currency-uom";
    return "/currency-uom";
  }, [location.pathname]);

  const endpoints = useMemo(
    () => ({
      list: "/currency-uom",
      create: "/currency-uom",
      edit: (id) => `/currency-uom/${id}`,
      update: (id) => `/currency-uom/${id}`,
      delete: (id) => `/currency-uom/${id}`,
    }),
    []
  );

  const {
    rows,
    isLoading,
    isSubmitting,
    error,
    filters,
    pagination,
    sortConfig,
    askDelete,
    changePage,
    changeSort,
    updateFilters,
    refresh,
  } = useCrud({
    endpoints,
    idKey: "currencyUOMId",
    updateMethod: "patch",
    initialFilters: { search: "" },
    initialSort: { key: "currencyName", order: "ASC" },
  });

  const tableColumns = useMemo(
    () => [
      { key: "currencyName", label: "Currency Name", sortable: true },
      { key: "currencyCode", label: "Code", sortable: true },
      { key: "symbol", label: "Symbol", sortable: true },
      {
        key: "status",
        label: "Status",
        render: (item) => (
          <span
            className={`rounded-full px-2 py-1 text-xs font-semibold ${
              item?.isActive
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
            }`}
          >
            {item?.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    if (!isEditPage || !id) {
      setEditValues(null);
      setFormError(null);
      return;
    }

    let isMounted = true;
    const loadEditData = async () => {
      setIsLoadingEdit(true);
      setFormError(null);
      try {
        const response = await apiClient.get(`/currency-uom/${id}`);
        const payload = response?.data || {};
        const data = payload?.data || payload || {};
        if (!isMounted) return;
        setEditValues(data);
      } catch (err) {
        if (!isMounted) return;
        const normalized = normalizeApiError(err);
        setFormError(normalized.message);
      } finally {
        if (isMounted) setIsLoadingEdit(false);
      }
    };

    loadEditData();
    return () => {
      isMounted = false;
    };
  }, [id, isEditPage]);

  const handleSave = async (values) => {
    setFormError(null);
    try {
      if (formMode === "edit" && id) {
        await apiClient.patch(`/currency-uom/${id}`, values);
        alertService.success("Record updated successfully.");
      } else {
        await apiClient.post("/currency-uom", values);
        alertService.success("Record created successfully.");
      }
      await refresh();
      navigate(basePath, { replace: true });
    } catch (err) {
      const normalized = normalizeApiError(err);
      setFormError(normalized.message);
      alertService.error(normalized.message);
    }
  };

  const openCreate = () => navigate(`${basePath}/new`);
  const openEdit = (item) => navigate(`${basePath}/${encrypt(item?.currencyUOMId)}/edit`);

  if (isFormPage) {
    const notFound = formMode === "edit" && !isLoadingEdit && !editValues && !formError;

    return (
      <div className="space-y-3">
        <div className="flex justify-end">
          <Button variant="secondary" onClick={() => navigate(basePath)}>
            Back to List
          </Button>
        </div>

        {formError ? <ErrorAlert message={formError} /> : null}
        {notFound ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">Record not found.</div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <CrudForm
              mode={formMode}
              schema={currencyUomFormSchema}
              initialValues={formMode === "edit" ? editValues || {} : { isActive: true }}
              isSubmitting={isSubmitting || isLoadingEdit}
              onSubmit={handleSave}
              onCancel={() => navigate(basePath)}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error ? <ErrorAlert message={error} /> : null}

      <CrudTable
        columns={tableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        onEdit={openEdit}
        onDelete={askDelete}
        searchKey="search"
        searchPlaceholder="Search by currency name or code"
        tableScrollHeight="52vh"
        toolbarAction={<Button onClick={openCreate}>Create Currency</Button>}
      />
    </div>
  );
}

export default CurrencyUomModulePage;
